import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
    Modal,
    Button,
    CardMedia,
    Input,
    Select,
    MenuItem,
    FormControl,
    SelectChangeEvent,
    Switch,
    FormControlLabel,
} from "@mui/material";

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PlayForWorkOutlinedIcon from '@mui/icons-material/PlayForWorkOutlined';

interface ModalProps {
    open: boolean;
    onClose: () => void;
    setOpen: Dispatch<SetStateAction<boolean>>;
}

const KPI_MAPPING: Record<string, string[]> = {
    "Executive Overview": [
        "Total GMV",
        "Net Revenue (after commissions, fees, discounts)",
        "Active Vendors",
        "Active Service Providers",
        "Active Customers (30d)",
        "Platform Growth Rate (%)",
        "Community → Revenue Contribution (%) (AI-attributed)"
    ],
    "Revenue & Monetization KPIs": [
        "Subscription Revenue",
        "Commission Revenue",
        "Platform Fees Revenue",
        "Avg Commission Rate",
        "ARPU (Average Revenue per User)",
        "Revenue by Plan (Free / Basic / Premium / Enterprise)",
        "Revenue Leakage (AI: refunds + disputes + failed charges)"
    ],
    "Marketplace Supply Health": [
        "Total Products",
        "Active Listings",
        "Low Stock Products",
        "Out of Stock Products",
        "Inventory Value",
        "Stock Accuracy (%) (AI / reconciliation-based)",
        "Catalog Quality Score (AI: images, attributes completeness)"
    ],
    "Orders & Fulfillment": [
        "Total Orders",
        "Pending Orders",
        "Processing Orders",
        "Delivered Orders",
        "Order Fulfillment Rate (%)",
        "Avg Order Processing Time",
        "Order Failure Rate (AI anomaly detection)"
    ],
    "Services & Bookings": [
        "Total Bookings",
        "Ongoing Services",
        "Completed Services",
        "Cancelled Services",
        "Booking Conversion Rate",
        "Avg Completion Time",
        "Service SLA Compliance (%) (AI)"
    ],
    "Returns, Refunds & Trust": [
        "Return Requests",
        "Pending Refunds",
        "Approved Refunds",
        "Refund Value",
        "Refund Rate (%)",
        "Dispute Rate",
        "High-Risk Vendors / Products (AI score)"
    ],
    "Community & Engagement": [
        "Active Community Members (30d)",
        "Total Interactions (messages, follows, reviews, shares)",
        "Top Engaged Customers (AI score)",
        "Top Engaged Vendors / SPs",
        "Community Conversion Rate (AI-attributed GMV)",
        "Influencer / Advocate Count (AI)"
    ],
    "AI Performance & Optimization": [
        "AI Recommendations Generated",
        "AI Recommendations Applied",
        "Auto-Optimizations Active",
        "Revenue Lift from AI (%)",
        "Churn Risk Accounts (customers / vendors)",
        "Pricing Risk Alerts (AI)"
    ],
    "Pricing, Fees & Commissions Control": [
        "Active Commission Structures",
        "Vendors on Custom Rates",
        "Platform Fee Coverage (%)",
        "Discount Impact on Revenue",
        "Pricing Competitiveness Score (AI benchmark)"
    ],
    "Subscription & Plan Health": [
        "Total Subscriptions",
        "Paid vs Free Ratio",
        "Upgrade Rate",
        "Downgrade Rate",
        "Subscription Churn",
        "Expired Subscriptions",
        "Plan Utilization Score (AI: feature usage vs plan)"
    ],
    "Marketing & Campaign Performance": [
        "Active Campaigns",
        "Campaign Conversion Rate",
        "Campaign ROI",
        "Cost per Acquisition (CPA)",
        "AI-Recommended Campaigns",
        "Best Performing Segment (AI)"
    ],
    "Risk, Governance & Platform Safety": [
        "Flagged Accounts",
        "Abuse / Fraud Alerts",
        "Suspicious Activity Score (AI)",
        "Policy Violations",
        "Manual Reviews Pending",
        "Compliance Health Score"
    ],
    "Operational Efficiency": [
        "Admin Actions Pending",
        "Avg Resolution Time",
        "Support Tickets Open",
        "AI-Resolved Issues (%)",
        "Manual Override Rate"
    ],
    "System & Data Health": [
        "Data Freshness Status",
        "Integration Health",
        "AI Model Confidence Score",
        "Failed Jobs / Pipelines",
        "Reporting Accuracy Index"
    ],
};

const TYPES = Object.keys(KPI_MAPPING);

export default function AddCard({ open, onClose, setOpen }: ModalProps) {
    const [image, setImage] = useState<string>("");
    const [fileName, setFileName] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");
    const [selectedKpi, setSelectedKpi] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    const [drillDown, setDrillDown] = useState<boolean>(true);
    const [aiWhy, setAiWhy] = useState<boolean>(true);
    const [actionCta, setActionCta] = useState<string>("Optimize");

    const handleTypeChange = (event: SelectChangeEvent) => {
        setSelectedType(event.target.value);
        setSelectedKpi(""); // Reset KPI when type changes
    };

    const handleKpiChange = (event: SelectChangeEvent) => {
        setSelectedKpi(event.target.value);
    };

    return (
        <Modal className="modal rejectReason_modal" open={open} onClose={onClose}>
            <div className="modal-dialog">
                <div className="modal-body">

                    {/* Header */}
                    <div className="modal_title">
                        <h2>Add Card</h2>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Form */}
                    <form className="form">

                        {/* Upload Image */}
                        {/* <div className="control_group">
                            <label>Card Icon</label>

                            {image ? (
                                <div className="upload_image">
                                    <div className="upload_image_holder no_radius v2">
                                        <figure>
                                            <CardMedia component="img" image={image} alt="card icon" />
                                        </figure>
                                        <CloseIcon
                                            onClick={() => {
                                                setImage("");
                                                setFileName("");
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <label className="upload_image">
                                    <Input
                                        sx={{ display: "none" }}
                                        type="file"
                                        inputProps={{ accept: "image/png,image/jpeg,image/svg+xml" }}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                            const files = event.target.files;
                                            if (files && files[0]) {
                                                setFileName(files[0].name);
                                                setImage(URL.createObjectURL(files[0]));
                                            }
                                        }}
                                    />
                                    <span className="upload_image_holder no_radius">
                                        <figure className="add_img">
                                            <img src="/static/images/placeholder.png" alt="upload" />
                                            <span className="upload_img_icon">
                                                <img src="/static/images/edit2_icon.svg" alt="edit" />
                                            </span>
                                        </figure>
                                    </span>
                                </label>
                            )}
                        </div> */}

                        {/* Type Selection */}
                        <div className="control_group">
                            <label htmlFor="card-type">Select Type</label>
                            <FormControl fullWidth>
                                <Select
                                    value={selectedType}
                                    onChange={handleTypeChange}
                                    displayEmpty
                                    size="small"
                                    inputProps={{ id: "card-type" }}
                                >
                                    <MenuItem value="" disabled>Select Type</MenuItem>
                                    {TYPES.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {/* KPI Metrics */}
                        <div className="control_group">
                            <label htmlFor="card-kpi">KPI Metrics</label>
                            <FormControl fullWidth disabled={!selectedType}>
                                <Select
                                    value={selectedKpi}
                                    onChange={handleKpiChange}
                                    displayEmpty
                                    size="small"
                                    inputProps={{ id: "card-kpi" }}
                                >
                                    <MenuItem value="" disabled>Select KPI</MenuItem>
                                    {selectedType && KPI_MAPPING[selectedType].map((kpi) => (
                                        <MenuItem key={kpi} value={kpi}>
                                            {kpi}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {/* Action CTA */}
                        <div className="control_group">
                            <label htmlFor="card-action-cta">Action CTA</label>
                            <FormControl fullWidth>
                                <Select
                                    value={actionCta}
                                    onChange={(e) => setActionCta(e.target.value as string)}
                                    size="small"
                                    inputProps={{ id: "card-action-cta" }}
                                >
                                    <MenuItem value="Fix">Fix (Manual Task)</MenuItem>
                                    <MenuItem value="Optimize">Optimize (AI Suggestion)</MenuItem>
                                    <MenuItem value="Assign">Assign (Department)</MenuItem>
                                    <MenuItem value="Auto">Auto (AI Agent Action)</MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        {/* Title */}
                        <div className="control_group">
                            <label htmlFor="card-title">Title</label>
                            <TextField
                                placeholder="Enter card title"
                                fullWidth
                                hiddenLabel
                                size="small"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                inputProps={{ id: "card-title" }}
                            />
                        </div>

                        {/* Footer */}
                        <div className="footer_btn_flex">
                            <Button
                                className="btn"
                                variant="outlined"
                                onClick={() => setOpen(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="btn btn_primary"
                                onClick={() => setOpen(false)}
                            >
                                Create
                            </Button>
                        </div>

                    </form>
                </div>
            </div>
        </Modal>
    );
}
